<!-- src/components/View58.vue -->
<template>
  <div class="dashboard-section">
    <aside class="sidebar-panels">
      <section class="calendar-panel">
        <!-- Календарь -->
        <div class="calendar-header">
          <button @click="previousMonth" class="calendar-nav" title="Предыдущий месяц">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <div class="calendar-title" @click="goToToday" title="Перейти к сегодня">
            <span class="month">{{ monthName }}</span>
            <span class="year">{{ currentYear }}</span>
          </div>

          <button @click="nextMonth" class="calendar-nav" title="Следующий месяц">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        <div class="calendar-grid">
          <div class="weekdays">
            <span v-for="day in weekDays" :key="day">{{ day }}</span>
          </div>

          <div class="dates">
            <button
              v-for="date in calendarDates"
              :key="date.key"
              class="date-cell"
              :class="{
                today: date.isToday,
                selected: isSelected(date),
                'other-month': date.isOtherMonth,
                'has-events': date.conferences.length > 0,
                'has-live': hasLiveConference(date),
                'has-past': hasPastConferences(date) && !hasUpcomingConferences(date),
              }"
              @click="selectDate(date)"
            >
              <span class="day-num">{{ date.day }}</span>

              <div class="conf-dots" v-if="date.conferences.length > 0">
                <span
                  v-for="(conf, i) in date.conferences.slice(0, 3)"
                  :key="conf.id || i"
                  class="conf-dot"
                  :style="{ backgroundColor: getDotBackgroundColor(conf) }"
                  :class="getDotClass(conf)"
                ></span>
                <span v-if="date.conferences.length > 3" class="more-dots">
                  +{{ date.conferences.length - 3 }}
                </span>
              </div>
            </button>
          </div>
        </div>

        <!-- Кнопка "Сегодня" если не на текущем месяце -->
        <div v-if="!isCurrentMonth" class="go-today-wrapper">
          <button class="go-today-btn" @click="goToToday">
            ← Сегодня
          </button>
        </div>

        <!-- Состояние загрузки -->
        <div v-if="loading" class="calendar-loading">
          <div class="spinner-small"></div>
          <span>Загрузка...</span>
        </div>

        <!-- Пустое состояние -->
        <div v-else-if="conferences.length === 0" class="calendar-empty">
          <span>📅 Нет конференций</span>
        </div>

        <!-- Легенда -->
        <div v-else class="upcoming-legend">
          <h4 class="legend-title">
            Ближайшие события
            <span v-if="liveCount > 0" class="live-count">{{ liveCount }} в эфире</span>
          </h4>

          <div v-if="upcomingConferences.length === 0" class="no-conferences">
            <span>Нет запланированных конференций</span>
          </div>

          <div v-else class="legend-list">
            <div
              v-for="conf in upcomingConferences"
              :key="conf.id"
              class="legend-item"
              :class="getLegendItemClass(conf)"
              @click="handleConferenceClick(conf)"
            >
              <span
                class="legend-dot"
                :style="{ backgroundColor: getDotBackgroundColor(conf) }"
                :class="{ pulse: isLive(conf) }"
              ></span>

              <div class="legend-info">
                <span class="legend-course">{{ conf.courseTitle || conf.course_title || 'Курс' }}</span>
                <div class="legend-datetime">
                  <span class="legend-date">{{ formatDateShort(conf.scheduled_at) }}</span>
                  <span class="legend-time">{{ formatTime(conf.scheduled_at) }}</span>
                </div>
              </div>

              <span class="legend-status" :class="getStatusClass(conf)">
                {{ getStatusLabel(conf) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Детали выбранного дня -->
        <transition name="slide-up">
          <div v-if="selectedDayConferences.length" class="selected-day">
            <h4 class="selected-day-title">
              <span>{{ formatSelectedDate }}</span>
              <button class="close-selected" @click="selectedDate = null">×</button>
            </h4>
            <div class="day-events">
              <div
                v-for="conf in selectedDayConferences"
                :key="conf.id"
                class="day-event"
                :class="getDayEventClass(conf)"
                @click="handleConferenceClick(conf)"
              >
                <span class="ev-dot" :style="{ backgroundColor: getDotBackgroundColor(conf) }"></span>
                <div class="ev-info">
                  <span class="ev-time">{{ formatTime(conf.scheduled_at) }}</span>
                  <span class="ev-title">{{ conf.title }}</span>
                  <span class="ev-course">{{ conf.courseTitle || conf.course_title || 'Курс' }}</span>
                </div>
                
                <!-- Кликабельная кнопка для live/скоро -->
                <button
                  v-if="canJoin(conf)"
                  class="ev-action-btn"
                  :class="getStatusClass(conf)"
                  @click.stop="joinConference(conf)"
                >
                  {{ getActionLabel(conf) }}
                </button>
                <span v-else class="ev-status" :class="getStatusClass(conf)">
                  {{ getStatusLabel(conf) }}
                </span>
              </div>
            </div>
          </div>
        </transition>
      </section>
    </aside>
  </div>
</template>

<script>
// import { getCourseColor, normalizeId } from '@/utils/conferenceColors'

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

const JOIN_WINDOW_MINUTES = 30
const SOON_WINDOW_MINUTES = 60

export default {
  name: 'View58',

  props: {
    /**
     * Конференции с цветами от родителя
     */
    conferences: {
      type: Array,
      default: () => [],
    },
    
    /**
     * Флаг загрузки
     */
    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['join-conference'],

  data() {
    return {
      currentDate: new Date(),
      selectedDate: null,
      weekDays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    }
  },

  computed: {
    /**
     * Ближайшие конференции (сортировка: live → скоро → будущие)
     */
    upcomingConferences() {
      const now = Date.now()

      return this.conferences
        .filter(conf => {
          if (!conf.scheduled_at) return false
          if (conf.status === 'cancelled') return false
          
          // Показываем live и будущие
          if (this.isLive(conf)) return true
          if (this.canJoinSoon(conf)) return true
          
          const scheduled = new Date(conf.scheduled_at).getTime()
          return scheduled > now
        })
        .sort((a, b) => {
          // Приоритет: live → скоро начнётся → будущие
          const getPriority = (conf) => {
            if (this.isLive(conf)) return 0
            if (this.canJoinSoon(conf)) return 1
            if (this.isSoon(conf)) return 2
            return 3
          }
          
          const priorityA = getPriority(a)
          const priorityB = getPriority(b)
          
          if (priorityA !== priorityB) {
            return priorityA - priorityB
          }
          
          // Внутри приоритета — по дате
          return new Date(a.scheduled_at) - new Date(b.scheduled_at)
        })
        .slice(0, 5)
    },

    /**
     * Количество live конференций
     */
    liveCount() {
      return this.conferences.filter(conf => this.isLive(conf)).length
    },

    /**
     * Проверка: отображается ли текущий месяц
     */
    isCurrentMonth() {
      const today = new Date()
      return (
        this.currentDate.getMonth() === today.getMonth() &&
        this.currentDate.getFullYear() === today.getFullYear()
      )
    },

    currentYear() {
      return this.currentDate.getFullYear()
    },

    currentMonth() {
      return this.currentDate.getMonth()
    },

    monthName() {
      return MONTHS[this.currentMonth]
    },

    calendarDates() {
      const dates = []
      const year = this.currentYear
      const month = this.currentMonth
      const today = new Date()

      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)

      let startDay = firstDay.getDay() - 1
      if (startDay < 0) startDay = 6

      const prevLast = new Date(year, month, 0).getDate()
      for (let i = startDay - 1; i >= 0; i--) {
        const d = new Date(year, month - 1, prevLast - i)
        dates.push(this.makeDateObj(d, today, true))
      }

      for (let day = 1; day <= lastDay.getDate(); day++) {
        const d = new Date(year, month, day)
        dates.push(this.makeDateObj(d, today, false))
      }

      const rem = 42 - dates.length
      for (let day = 1; day <= rem; day++) {
        const d = new Date(year, month + 1, day)
        dates.push(this.makeDateObj(d, today, true))
      }

      return dates
    },

    selectedDayConferences() {
      if (!this.selectedDate) return []
      const dateStr = this.toDateString(this.selectedDate)
      
      return this.conferences
        .filter(c => {
          if (!c.scheduled_at) return false
          return this.toDateString(new Date(c.scheduled_at)) === dateStr
        })
        .sort((a, b) => {
          // Сначала live, потом по времени
          if (this.isLive(a) && !this.isLive(b)) return -1
          if (!this.isLive(a) && this.isLive(b)) return 1
          return new Date(a.scheduled_at) - new Date(b.scheduled_at)
        })
    },

    formatSelectedDate() {
      if (!this.selectedDate) return ''
      return this.selectedDate.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        weekday: 'long',
      })
    },
  },

  methods: {
    // ==================== УТИЛИТЫ ДАТА/ВРЕМЯ ====================
    
    toDateString(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    makeDateObj(date, today, isOtherMonth) {
      const key = this.toDateString(date)
      const conferences = this.getConfsForDate(date)

      return {
        date,
        key,
        day: date.getDate(),
        isToday: this.isSameDay(date, today),
        isOtherMonth,
        conferences,
      }
    },

    getConfsForDate(date) {
      const dateStr = this.toDateString(date)
      return this.conferences.filter(c => {
        if (!c.scheduled_at) return false
        return this.toDateString(new Date(c.scheduled_at)) === dateStr
      })
    },

    isSameDay(d1, d2) {
      return (
        d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear()
      )
    },

    // ==================== СТАТУСЫ КОНФЕРЕНЦИЙ ====================

    /**
     * Проверка: идёт ли сейчас
     */
    isLive(conf) {
      if (
        conf.status === 'completed' ||
        conf.status === 'ended' ||
        conf.status === 'cancelled' ||
        conf.ended_at
      ) {
        return false
      }

      if (conf.status === 'live' || conf.status === 'in_progress') return true

      return Boolean(conf.started_at)
    },

    /**
     * 🔥 ИСПРАВЛЕНО: Проверка завершённости по времени
     */
    isEnded(conf) {
      if (
        conf.status === 'ended' ||
        conf.status === 'completed' ||
        conf.status === 'cancelled' ||
        conf.ended_at
      )
        return true
      if (this.isLive(conf)) return false
      
      const now = Date.now()
      const scheduled = new Date(conf.scheduled_at).getTime()
      const duration = (conf.duration_minutes || 60) * 60 * 1000
      const endTime = scheduled + duration
      
      return now > endTime
    },

    /**
     * Проверка: скоро начнётся (в течение часа)
     */
    isSoon(conf) {
      if (this.isLive(conf)) return false
      if (this.isEnded(conf)) return false

      const diff = new Date(conf.scheduled_at).getTime() - Date.now()
      const soonMs = SOON_WINDOW_MINUTES * 60 * 1000
      const joinMs = JOIN_WINDOW_MINUTES * 60 * 1000
      return diff <= soonMs && diff > joinMs
    },

    /**
     * Проверка: можно присоединиться (за 15 минут до начала)
     */
    canJoinSoon(conf) {
      if (this.isLive(conf)) return false
      if (this.isEnded(conf)) return false
      if (conf.status === 'cancelled') return false
      
      const now = Date.now()
      const scheduled = new Date(conf.scheduled_at).getTime()
      const duration = (conf.duration_minutes || 60) * 60 * 1000
      const joinStart = scheduled - JOIN_WINDOW_MINUTES * 60 * 1000
      const joinEnd = scheduled + duration
      return now >= joinStart && now <= joinEnd
    },

    /**
     * Можно ли присоединиться к конференции
     */
    canJoin(conf) {
      return this.isLive(conf) || this.canJoinSoon(conf)
    },

    /**
     * Есть ли live конференция в этот день
     */
    hasLiveConference(dateObj) {
      return dateObj.conferences.some(c => this.isLive(c))
    },

    /**
     * Есть ли прошедшие конференции в этот день
     */
    hasPastConferences(dateObj) {
      return dateObj.conferences.some(c => this.isEnded(c))
    },

    /**
     * Есть ли предстоящие конференции в этот день
     */
    hasUpcomingConferences(dateObj) {
      return dateObj.conferences.some(c => !this.isEnded(c) && !this.isLive(c))
    },

    // ==================== КЛАССЫ И МЕТКИ ====================

    /**
     * Цвет точки (серый для завершённых)
     */
    getDotBackgroundColor(conf) {
      if (this.isEnded(conf) || conf.status === 'cancelled') {
        return '#636363'
      }
      return conf.color || '#0096ed'
    },

    getDotClass(conf) {
      if (this.isLive(conf)) return 'pulse'
      if (conf.status === 'cancelled') return 'cancelled'
      if (this.isEnded(conf)) return 'ended'
      if (this.canJoinSoon(conf)) return 'join-soon'
      if (this.isSoon(conf)) return 'soon'
      return ''
    },

    getStatusClass(conf) {
      if (this.isLive(conf)) return 'live'
      if (conf.status === 'cancelled') return 'cancelled'
      if (this.isEnded(conf)) return 'ended'
      if (this.canJoinSoon(conf)) return 'join'
      if (this.isSoon(conf)) return 'soon'
      return 'scheduled'
    },

    /**
     * 🔥 ИСПРАВЛЕНО: Правильные метки статусов
     */
    getStatusLabel(conf) {
      if (this.isLive(conf)) return 'В эфире'
      if (conf.status === 'cancelled') return 'Отменено'
      if (this.isEnded(conf)) return 'Завершено'
      if (this.canJoinSoon(conf)) return 'Войти'
      if (this.isSoon(conf)) return 'Скоро'
      return 'Запланировано'
    },

    /**
     * Метка для кнопки действия
     */
    getActionLabel(conf) {
      if (this.isLive(conf)) return 'В эфире'
      if (this.canJoinSoon(conf)) return 'Войти'
      return 'Открыть'
    },

    /**
     * Класс для элемента легенды
     */
    getLegendItemClass(conf) {
      return {
        'is-live': this.isLive(conf),
        'is-soon': this.isSoon(conf) && !this.isLive(conf),
        'is-join': this.canJoinSoon(conf),
        'is-ended': this.isEnded(conf),
        'is-clickable': this.canJoin(conf),
      }
    },

    /**
     * Класс для события дня
     */
    getDayEventClass(conf) {
      return {
        live: this.isLive(conf),
        ended: this.isEnded(conf),
        cancelled: conf.status === 'cancelled',
        clickable: this.canJoin(conf),
      }
    },

    // ==================== ФОРМАТИРОВАНИЕ ====================

    formatTime(dateStr) {
      if (!dateStr) return '--:--'
      return new Date(dateStr).toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatDateShort(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      if (this.isSameDay(date, today)) return 'Сегодня'
      if (this.isSameDay(date, tomorrow)) return 'Завтра'
      if (this.isSameDay(date, yesterday)) return 'Вчера'

      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
      })
    },

    // ==================== НАВИГАЦИЯ ====================

    previousMonth() {
      this.currentDate = new Date(this.currentYear, this.currentMonth - 1, 1)
    },

    nextMonth() {
      this.currentDate = new Date(this.currentYear, this.currentMonth + 1, 1)
    },

    goToToday() {
      this.currentDate = new Date()
      this.selectedDate = new Date()
    },

    selectDate(dateObj) {
      if (this.selectedDate && this.isSameDay(dateObj.date, this.selectedDate)) {
        this.selectedDate = null
        return
      }

      this.selectedDate = dateObj.date

      if (dateObj.isOtherMonth) {
        this.currentDate = new Date(
          dateObj.date.getFullYear(),
          dateObj.date.getMonth(),
          1
        )
      }
    },

    isSelected(dateObj) {
      if (!this.selectedDate) return false
      return this.isSameDay(dateObj.date, this.selectedDate)
    },

    // ==================== ДЕЙСТВИЯ ====================

    /**
     * Присоединиться к конференции
     */
    joinConference(conf) {
      this.$emit('join-conference', conf)
    },

    /**
     * Обработчик клика по конференции
     */
    handleConferenceClick(conf) {
      if (this.canJoin(conf)) {
        this.$emit('join-conference', conf)
      } else if (!this.isEnded(conf) && conf.status !== 'cancelled') {
        // Переход на страницу курса для будущих конференций
        if (conf.courseId || conf.course_id) {
          this.$router.push({
            name: 'CoursePreview',
            params: { id: conf.courseId || conf.course_id },
          })
        }
      }
    },
  },

  mounted() {
    console.log('📅 View58 mounted')
    console.log('📊 Conferences from props:', this.conferences?.length || 0)
  },

  watch: {
    conferences: {
      handler(newVal) {
        console.log('📅 View58: Conferences updated:', newVal?.length || 0)
        
        // Отладка статусов
        if (newVal?.length > 0) {
          newVal.forEach(conf => {
            console.log(`  📌 ${conf.title}: live=${this.isLive(conf)}, ended=${this.isEnded(conf)}, status=${conf.status}`)
          })
        }
      },
      immediate: true,
    },
  },
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* Основной контейнер */
.dashboard-section {
  display: flex;
  gap: 34px;
  /* padding: 20px; */
  max-width: 1348px;
  margin: 0 auto;
}

.sidebar-panels {
  width: 380px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ========== БЕЛАЯ ТЕМА КАЛЕНДАРЯ ========== */
.calendar-panel {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
}

/* Header */
.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
}

.calendar-nav {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
}

.calendar-nav:hover {
  background: #0096ed;
  border-color: #0096ed;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 150, 237, 0.3);
}

.calendar-nav:active {
  transform: translateY(0);
}

.calendar-title {
  flex: 1;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  transition: background 0.2s;
}

.calendar-title:hover {
  background: #f8f9fa;
}

.month {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #636363;
}

.year {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #6c757d;
}

/* Кнопка "Сегодня" */
.go-today-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.go-today-btn {
  padding: 8px 16px;
  background: #eaf6ff;
  border: 1px solid #0096ed;
  border-radius: 20px;
  color: #0096ed;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.go-today-btn:hover {
  background: #0096ed;
  color: white;
}

/* Calendar grid */
.calendar-grid {
  margin-bottom: 24px;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 12px;
}

.weekdays span {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #868e96;
  padding: 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dates {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

/* Ячейка даты */
.date-cell {
  aspect-ratio: 1;
  min-height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: #ffffff;
  border: 1px solid #f1f3f4;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
}

.date-cell:hover:not(.other-month) {
  /* background: #eaf6ff; */
  border-color: #0096ed;
  transform: scale(1.05);
}

.date-cell.other-month {
  opacity: 0.3;
  background: transparent;
  border-color: transparent;
}

.date-cell.other-month:hover {
  transform: none;
  background: transparent;
}

.date-cell.today {
  background: #e8f4fd;
  border-color: #0095ec;
}

.date-cell.today .day-num {
  color: #0095ec;
  font-weight: 700;
}

.date-cell.selected {
  background: linear-gradient(135deg, #0096ed, #0096ed);
  border-color: #0096ed;
  box-shadow: 0 4px 12px rgba(0, 150, 237, 0.35);
}

.date-cell.selected .day-num {
  color: #ffffff;
  font-weight: 700;
}

.date-cell.has-events {
  font-weight: 600;
}

/* 🔥 Новые стили для ячеек с live и прошедшими */
.date-cell.has-live {
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

.date-cell.has-past:not(.has-events) {
  opacity: 0.6;
}

.day-num {
  font-size: 14px;
  font-weight: 500;
  color: #636363;
  line-height: 1;
}

/* Точки конференций */
.conf-dots {
  display: flex;
  gap: 3px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  min-height: 6px;
}

.conf-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.2s;
}

.conf-dot.pulse {
  animation: pulse-dot 2s ease-in-out infinite;
}

.conf-dot.soon,
.conf-dot.join-soon {
  animation: glow-dot 2s ease-in-out infinite;
}

.conf-dot.cancelled,
.conf-dot.ended {
  opacity: 0.4;
}

@keyframes pulse-dot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.4);
    opacity: 0.6;
  }
}

@keyframes glow-dot {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
  }
  50% {
    box-shadow: 0 0 6px 2px rgba(245, 158, 11, 0.4);
  }
}

.more-dots {
  font-size: 8px;
  font-weight: 700;
  color: #868e96;
}

/* Легенда */
.upcoming-legend {
  padding: 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  margin-bottom: 16px;
}

.legend-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 14px;
}

.live-count {
  padding: 4px 10px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 700;
  border-radius: 12px;
  animation: pulse-badge 2s ease-in-out infinite;
}

.no-conferences {
  text-align: center;
  padding: 16px;
  color: #868e96;
  font-size: 13px;
}

.legend-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
}

.legend-list::-webkit-scrollbar {
  width: 4px;
}

.legend-list::-webkit-scrollbar-track {
  background: transparent;
}

.legend-list::-webkit-scrollbar-thumb {
  background: #dee2e6;
  border-radius: 2px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  cursor: default;
  transition: all 0.2s ease;
}

.legend-item.is-clickable {
  cursor: pointer;
}

.legend-item.is-clickable:hover {
  background: #eaf6ff;
  border-color: #0096ed;
}

.legend-item.is-live {
  background: #fef2f2;
  border-color: #fecaca;
}

.legend-item.is-soon,
.legend-item.is-join {
  background: #fffbeb;
  border-color: #fde68a;
}

.legend-item.is-ended {
  opacity: 0.6;
  background: #f8f9fa;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-dot.pulse {
  animation: pulse-dot 2s ease-in-out infinite;
}

.legend-info {
  flex: 1;
  min-width: 0;
}

.legend-course {
  display: block;
  font-weight: 500;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.legend-datetime {
  display: flex;
  gap: 10px;
  font-size: 12px;
  margin-top: 2px;
}

.legend-date {
  font-weight: 600;
  color: #636363;
}

.legend-time {
  color: #6c757d;
}

/* 🔥 Статусы в легенде */
.legend-status {
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 6px;
  white-space: nowrap;
}

.legend-status.live {
  background: #ef4444;
  color: white;
  animation: pulse-badge 2s ease-in-out infinite;
}

.legend-status.join {
  background: #27ae60;
  color: white;
}

.legend-status.soon {
  background: #f59e0b;
  color: white;
}

.legend-status.scheduled {
  background: #e9ecef;
  color: #6c757d;
}

.legend-status.ended {
  background: #f1f3f4;
  color: #636363;
}

.legend-status.cancelled {
  background: #fef2f2;
  color: #ef4444;
}

@keyframes pulse-badge {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Выбранный день */
.selected-day {
  padding: 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
}

.selected-day-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 14px;
  color: #636363;
  text-transform: capitalize;
}

.close-selected {
  width: 26px;
  height: 26px;
  border: none;
  background: #e9ecef;
  border-radius: 50%;
  font-size: 18px;
  color: #495057;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-selected:hover {
  background: #fecaca;
  color: #ef4444;
}

.day-events {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.day-events::-webkit-scrollbar {
  width: 4px;
}

.day-events::-webkit-scrollbar-track {
  background: transparent;
}

.day-events::-webkit-scrollbar-thumb {
  background: #dee2e6;
  border-radius: 2px;
}

.day-event {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  cursor: default;
  transition: all 0.2s ease;
}

.day-event.clickable {
  cursor: pointer;
}

.day-event.clickable:hover {
  background: #eaf6ff;
  border-color: #0096ed;
  box-shadow: 0 2px 8px rgba(0, 150, 237, 0.15);
}

.day-event.live {
  background: #fef2f2;
  border-color: #fecaca;
}

.day-event.ended {
  opacity: 0.6;
  background: #f8f9fa;
}

.day-event.cancelled {
  opacity: 0.5;
  background: #f8f9fa;
  text-decoration: line-through;
}

.ev-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ev-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.ev-time {
  font-size: 14px;
  font-weight: 600;
  color: #636363;
}

.ev-title {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ev-course {
  font-size: 11px;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Статус/Кнопка */
.ev-status {
  font-size: 10px;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  white-space: nowrap;
}

.ev-status.live {
  background: #ef4444;
  color: white;
}

.ev-status.join {
  background: #27ae60;
  color: white;
}

.ev-status.soon {
  background: #f59e0b;
  color: white;
}

.ev-status.scheduled {
  background: #e9ecef;
  color: #6c757d;
}

.ev-status.ended {
  background: #f1f3f4;
  color: #636363;
}

.ev-status.cancelled {
  background: #fef2f2;
  color: #ef4444;
}

/* 🔥 Кнопка действия */
.ev-action-btn {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.ev-action-btn.live {
  background: #ef4444;
  color: white;
  animation: pulse-btn 2s ease-in-out infinite;
}

.ev-action-btn.live:hover {
  background: #dc2626;
  transform: scale(1.05);
}

.ev-action-btn.join {
  background: #27ae60;
  color: white;
}

.ev-action-btn.join:hover {
  background: #219a52;
  transform: scale(1.05);
}

@keyframes pulse-btn {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
  }
}

/* Анимация */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Состояния */
.calendar-loading,
.calendar-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #666;
  font-size: 14px;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid #e0e0e0;
  border-top-color: #0096ed;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
